import fs from 'node:fs'
import path from 'node:path'
import readline from 'node:readline'
import type { RemoveSchemaCommandOptions } from '@core-engine/commands/runtime.js'
import { loadSchema } from '@core-engine/schema/schema-reader.js'
import {
  BARREL_SCOPE,
  clearTombstone,
  deleteSnapshot,
  hasTombstone,
  loadManifest,
  snapshotRootExists,
  writeTombstone
} from '@core-engine/snapshots/store.js'
import { toKebabCase } from '@core-engine/utils/string.js'
import { resolveProjectPaths } from '@next-adapter/config/paths.js'
import { resolveConfig } from '@next-adapter/config.js'
import { applyGeneratedFiles } from '@next-adapter/snapshots/apply.js'
import { renderBarrelFiles } from '@next-adapter/snapshots/barrels.js'
import { getSchemaOwner } from '@preset-engine/ownership.js'

async function promptConfirm(message: string): Promise<boolean> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout
  })

  return new Promise((resolve) => {
    rl.question(`${message} (y/N) `, (answer) => {
      rl.close()
      resolve(answer.toLowerCase() === 'y' || answer.toLowerCase() === 'yes')
    })
  })
}

function removePath(cwd: string, filePath: string): boolean {
  const fullPath = path.join(cwd, ...filePath.split('/'))
  const existed = fs.existsSync(fullPath)
  fs.rmSync(fullPath, { recursive: true, force: true })
  return existed
}

function cleanupEmptyDirs(
  cwd: string,
  deletedPaths: string[],
  configPaths: ReturnType<typeof resolveProjectPaths>
): void {
  const stopRoots = new Set([
    path.join(cwd, ...configPaths.adminDir.split('/')),
    path.join(cwd, ...configPaths.adminNavigationDir.split('/')),
    path.join(cwd, ...configPaths.adminWebhookEventsDir.split('/')),
    path.join(cwd, ...configPaths.pagesDir.split('/'))
  ])

  for (const deletedPath of deletedPaths) {
    let current = path.dirname(path.join(cwd, ...deletedPath.split('/')))

    while (!stopRoots.has(current)) {
      if (!fs.existsSync(current)) {
        current = path.dirname(current)
        continue
      }

      const entries = fs.readdirSync(current)
      if (entries.length > 0) {
        break
      }

      fs.rmdirSync(current)
      current = path.dirname(current)
    }
  }
}

function resolveSchemaOwnerForRemoval(
  cwd: string,
  schemaName: string
): 'core' | 'user' | `preset:${string}` {
  const explicitOwner = getSchemaOwner(cwd, schemaName)
  if (explicitOwner) {
    return explicitOwner
  }

  if (schemaName === 'settings') {
    return 'core'
  }

  return 'user'
}

export async function runRemoveSchemaCommand(
  schemaName: string,
  options: RemoveSchemaCommandOptions
): Promise<void> {
  const owner = resolveSchemaOwnerForRemoval(
    options.cwd ? path.resolve(options.cwd) : process.cwd(),
    schemaName
  )
  if (owner === 'core') {
    console.error(
      `  "${schemaName}" is a core Admin module and cannot be removed with remove-schema.`
    )
    process.exit(1)
  }
  if (owner.startsWith('preset:')) {
    console.error(`  "${schemaName}" is owned by ${owner}. Remove the owning preset instead.`)
    process.exit(1)
  }

  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const config = await resolveConfig(cwd)
  const paths = resolveProjectPaths(config)
  const manifest = loadManifest(cwd, schemaName)

  if (!snapshotRootExists(cwd)) {
    console.error(
      '  Project state is unsupported or incomplete. Re-run `betterstart init --force` to create the current snapshot-backed layout.'
    )
    process.exit(1)
  }

  if (!manifest) {
    console.error(
      '  Project state is unsupported or incomplete. Re-run `betterstart init --force` to create the current snapshot-backed layout.'
    )
    process.exit(1)
  }

  if (!options.force) {
    const confirmed = await promptConfirm(`  Remove generated files for ${schemaName}?`)
    if (!confirmed) {
      console.log('\n  Cancelled.\n')
      return
    }
  }

  const deletedPaths: string[] = []

  for (const file of [...manifest.files.map((entry) => entry.path), ...manifest.skipped]) {
    if (removePath(cwd, file)) {
      deletedPaths.push(file)
    }
  }

  const loaded = (() => {
    try {
      return loadSchema(path.join(cwd, ...paths.schemasDir.split('/')), schemaName)
    } catch {
      return null
    }
  })()

  const kebabName = toKebabCase(schemaName)
  if (loaded?.type === 'form') {
    if (removePath(cwd, `${paths.adminActionsDir}/${schemaName}`)) {
      deletedPaths.push(`${paths.adminActionsDir}/${schemaName}`)
    }
    if (removePath(cwd, `${paths.pagesDir}/forms/${kebabName}`)) {
      deletedPaths.push(`${paths.pagesDir}/forms/${kebabName}`)
    }
  } else {
    if (removePath(cwd, `${paths.adminActionsDir}/${schemaName}`)) {
      deletedPaths.push(`${paths.adminActionsDir}/${schemaName}`)
    }
    if (removePath(cwd, `${paths.pagesDir}/${schemaName}`)) {
      deletedPaths.push(`${paths.pagesDir}/${schemaName}`)
    }
  }

  cleanupEmptyDirs(cwd, deletedPaths, paths)
  deleteSnapshot(cwd, schemaName)
  if (hasTombstone(cwd, schemaName)) {
    clearTombstone(cwd, schemaName)
  }
  writeTombstone(cwd, schemaName)

  await applyGeneratedFiles({
    cwd,
    config,
    scope: BARREL_SCOPE,
    schemaJson: { name: BARREL_SCOPE },
    generatedFiles: renderBarrelFiles(cwd, config),
    force: false,
    interactive: false
  })

  console.log(`\n  Removed generated files for ${schemaName}.`)
  console.log(`  Tombstone written: .betterstart/snapshots/_removed/${schemaName}`)
  console.log('  Schema JSON preserved.')
  console.log('')
}
